import React, { useState } from 'react';
import { DocumentationFile } from '../../../../graphql/graphql';
import EcardEditFileModal from '../ECardEditFile/ECardEditFileModal';
import { FormattedDateTime } from '../../../../Components/FormattedDate';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import Button from '../../../../Components/Button/Button';
import AntivirusCheck from '../../../../Components/Base/Loading/AntivirusCheck';
import useTranslation from '../../../../Hooks/useTranslation';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import { prepareDocumentationFileForMediaViewer } from '../../Services/eCardFile.service';
import { useAppContext } from '../../../../Services/AppContext';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import { FileToShare } from '../ECard/ECard';
import useDocumentationFileTransfer from '../../Hooks/useDocumentationFileTransfer';

// @ts-ignore
import styles from './ECardListItem.module.scss';
import { ECardType } from '../../Enums/ECardType.enum';
import MultiMediaViewerModal from '../../../Base/Components/MultiMediaViewer/MultiMediaViewerModal';
import { IMultiMediaViewerItem } from '../../../Base/Components/MultiMediaViewer/MultiMediaViewerItem';

interface Props {
	item: DocumentationFile;
	reloadData: (folderId: string, needReload: boolean) => void;
	isShareMode: boolean;
	onCheck: (file: FileToShare, isChecked: boolean) => void;
	isSelected: boolean;
	canBeEdited: boolean;
	eCardType: ECardType;
}

const ECardListItem: React.FC<Props> = ({
	item,
	reloadData,
	isShareMode,
	onCheck,
	isSelected,
	canBeEdited,
	eCardType,
}) => {
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [isFileOpen, setIsFileOpen] = useState(false);
	const { t } = useTranslation();
	const { socketUrl } = useAppContext();
	const { transfersRecords } = useDocumentationFileTransfer(item, eCardType);

	const handleCheck = () => {
		onCheck({ id: item.id, name: item.name ?? '', file: item }, !isSelected);
	};

	const hasGroupFiles = false;

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.date}>
					<FormattedDateTime datetime={item.createdAt} />
				</div>
				<div className={styles.file}>
					{item.avResult === 'clean' && (
						<div onClick={() => setIsFileOpen(true)} className={styles.name}>
							{item.displayName}
						</div>
					)}
					{item.avResult !== 'clean' && (
						<div className={styles.name}>{item.displayName}</div>
					)}
					{((item.createdBy && !item.createdBy.isPatient) ||
						item.workplace) && (
						<div className={styles.savedBy}>
							{t('base.shared.inserted')}: {item.createdBy?.profile?.fullName}
							{item.workplace && item.createdBy && ', '}
							{item.workplace && t('base.shared.workplace')}{' '}
							{item.workplace?.name}
						</div>
					)}
					{item.createdBy && item.createdBy.isPatient && (
						<div className={styles.savedBy}>
							{t('base.shared.inserted')}: {item.createdBy?.profile?.fullName}
						</div>
					)}
					{transfersRecords?.map((transfer, key) => (
						<div className={styles.savedBy} key={key}>
							<span>{transfer.direction}</span> {transfer.text}
						</div>
					))}
				</div>
				{item.avResult === 'clean' && (
					<div className={styles.itemControls}>
						{isShareMode && (
							<Checkbox value={isSelected} onChange={handleCheck} />
						)}
						{canBeEdited && (
							<LinkButton
								onClick={() => setIsEditOpen(true)}
								title={'base.shared.edit'}
								theme={Theme.BLUE}
							/>
						)}
						<Button
							onClick={() => setIsFileOpen(true)}
							theme={Theme.GREEN}
							title={'base.details'}
						/>
					</div>
				)}
				{item.avResult !== 'clean' && (
					<div className={styles.itemControls}>
						<AntivirusCheck inRow={true} />
					</div>
				)}
			</div>
			{isEditOpen && (
				<EcardEditFileModal
					opened
					onClose={() => setIsEditOpen(false)}
					patientId={item.userId}
					item={item}
					onUpdate={reloadData}
				/>
			)}
			{isFileOpen && !hasGroupFiles && (
				<MediaViewerModal
					opened
					onClose={() => setIsFileOpen(false)}
					file={prepareDocumentationFileForMediaViewer(item, socketUrl)}
				/>
			)}
			{isFileOpen && hasGroupFiles && (
				<MultiMediaViewerModal
					files={item.groupFiles as IMultiMediaViewerItem[]}
					isOpen
					onClose={() => setIsFileOpen(false)}
				/>
			)}
		</>
	);
};

export default ECardListItem;
